import { useEffect, useMemo, useState } from "react";
import { Upload, Send, Download, Copy, RotateCw, XCircle, Users } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "../lib/api";
import { PageTitle, StatusBadge, Loader, Field } from "../components/kit";
import { InviteEmailEditor } from "../components/InviteEmailEditor";
import { InviteFunnel } from "../components/InviteFunnel";

const TEMPLATE = "email,contact_name,company_name,phone\njane@example-plumbing.co.uk,Jane Smith,Smith Plumbing Ltd,07700 900123\n";
const COLS = { email: /e-?mail/, contact_name: /contact|^name|full ?name|first/, company_name: /company|business|supplier|trading/, phone: /phone|mobile|tel/ };
const isEmail = (s) => /^[^@\s,;]+@[^@\s,;]+\.[^@\s,;]{2,}$/.test((s || "").trim());

const splitLine = (line) => {
  const sep = line.includes("\t") ? "\t" : ",";
  const out = []; let cur = "", q = false;
  for (const ch of line) { if (ch === '"') q = !q; else if (ch === sep && !q) { out.push(cur); cur = ""; } else cur += ch; }
  out.push(cur);
  return out.map((x) => x.trim());
};

const headerMap = (cells) => {
  const m = {};
  cells.forEach((c, i) => { const k = Object.keys(COLS).find((key) => !(key in m) && COLS[key].test(c.toLowerCase())); if (k) m[k] = i; });
  return "email" in m ? m : null;
};

const guessRow = (cells) => {
  const ei = cells.findIndex(isEmail);
  const rest = cells.filter((_, i) => i !== ei);
  return { email: cells[ei] || cells[0] || "", contact_name: rest[0] || "", company_name: rest[1] || "", phone: rest[2] || "" };
};

export const parseList = (text) => {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (!lines.length) return [];
  const hm = headerMap(splitLine(lines[0]));
  const body = hm ? lines.slice(1) : lines;
  return body.map(splitLine).map((c) => (hm ? Object.fromEntries(Object.entries(hm).map(([k, i]) => [k, c[i] || ""])) : guessRow(c)));
};

function Preview({ rows }) {
  const bad = rows.filter((r) => !isEmail(r.email)).length;
  return (
    <div className="mt-4" data-testid="invite-preview">
      <p className="text-sm"><b data-testid="invite-preview-count">{rows.length}</b> providers found{bad > 0 && <span className="text-rose-600"> · {bad} without a valid email (they will be skipped)</span>}</p>
      <div className="card overflow-x-auto mt-2 max-h-64"><table className="w-full text-xs"><thead className="bg-slate-50 text-left"><tr><th className="p-2">Email</th><th className="p-2">Contact</th><th className="p-2">Company</th><th className="p-2">Phone</th></tr></thead>
        <tbody>{rows.slice(0, 50).map((r, i) => <tr key={i} className={`border-t border-slate-100 ${isEmail(r.email) ? "" : "bg-rose-50"}`}><td className="p-2">{r.email}</td><td className="p-2">{r.contact_name}</td><td className="p-2">{r.company_name}</td><td className="p-2">{r.phone}</td></tr>)}</tbody></table>
        {rows.length > 50 && <p className="p-2 text-xs muted">…and {rows.length - 50} more</p>}</div>
    </div>
  );
}

function OfferFields({ f, setF, plans }) {
  return (
    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
      <Field label="Batch name"><input className="input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. FM App suppliers – wave 1" data-testid="invite-batch-name" /></Field>
      <Field label="Vetted on (shown in email)"><input className="input" value={f.source} onChange={(e) => setF({ ...f, source: e.target.value })} data-testid="invite-source" /></Field>
      <Field label="Free plan"><select className="input" value={f.plan} onChange={(e) => setF({ ...f, plan: e.target.value })} data-testid="invite-plan">{plans.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</select></Field>
      <Field label="Free months"><input type="number" min={1} max={24} className="input" value={f.months} onChange={(e) => setF({ ...f, months: e.target.value })} data-testid="invite-months" /></Field>
    </div>
  );
}

function Composer({ onSent }) {
  const [plans, setPlans] = useState([]);
  const [f, setF] = useState({ name: "", source: "Facilities Management.App", plan: "premium", months: 3 });
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [tpl, setTpl] = useState(null);
  const [meta, setMeta] = useState(null);
  useEffect(() => {
    api.get("/plans").then((r) => setPlans(r.data.filter((p) => p.monthly_price > 0)));
    api.get("/admin/invites/template").then((r) => { setTpl(r.data.template); setMeta(r.data); });
  }, []);
  const rows = useMemo(() => parseList(text), [text]);
  const onFile = (e) => { const file = e.target.files?.[0]; if (!file) return; const rd = new FileReader(); rd.onload = () => setText(String(rd.result)); rd.readAsText(file); e.target.value = ""; };
  const template = () => { const a = document.createElement("a"); a.href = URL.createObjectURL(new Blob([TEMPLATE], { type: "text/csv" })); a.download = "provider-invites-template.csv"; a.click(); };
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/invites", { ...f, months: +f.months, rows, template: tpl });
      setResult(data); toast.success(`${data.sent} invites sent`); if (data.sent) { setText(""); onSent(); }
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <section className="card p-5" data-testid="invite-composer">
      <h2 className="font-bold mb-1">Invite providers in bulk</h2>
      <p className="text-sm muted mb-4">Each provider gets an email with a personal sign-up link that lasts 30 days. When they join, they're approved straight away, shown as fully verified, and get the free plan below.</p>
      <OfferFields f={f} setF={setF} plans={plans} />
      <div className="flex flex-wrap gap-2 mt-4">
        <label className="btn-navy btn-sm cursor-pointer" data-testid="invite-csv-upload"><Upload size={14} />Upload CSV<input type="file" accept=".csv,.txt,text/csv" className="hidden" onChange={onFile} data-testid="invite-csv-input" /></label>
        <button onClick={template} className="btn-ghost btn-sm" data-testid="invite-template-btn"><Download size={14} />Download template</button>
      </div>
      <Field label="…or paste a list (one per line: email, contact name, company, phone, or copy straight from a spreadsheet)">
        <textarea className="input min-h-[120px] font-mono text-xs" value={text} onChange={(e) => setText(e.target.value)} placeholder={"jane@smithplumbing.co.uk, Jane Smith, Smith Plumbing Ltd\nbob@brightsparks.co.uk, Bob Jones, Bright Sparks Electrical"} data-testid="invite-paste" />
      </Field>
      {rows.length > 0 && <Preview rows={rows} />}
      {tpl && meta && <InviteEmailEditor tpl={tpl} setTpl={setTpl} offer={f} rows={rows} meta={meta} />}
      <button onClick={send} disabled={busy || !rows.length || !tpl} className="btn-coral mt-4" data-testid="invite-send-btn"><Send size={16} />{busy ? "Sending…" : `Send ${rows.length || ""} invites`}</button>
      {result && <div className="mt-4 text-sm" data-testid="invite-result"><p className="text-emerald-700 font-semibold">{result.sent} invites sent.</p>
        {result.skipped.length > 0 && <div className="mt-1"><p className="text-amber-700">{result.skipped.length} skipped:</p><ul className="text-xs muted list-disc pl-5">{result.skipped.slice(0, 30).map((s, i) => <li key={i} data-testid="invite-skipped-row">{s.email}: {s.reason}</li>)}</ul></div>}</div>}
    </section>
  );
}

function BatchChips({ batches, batch, setBatch }) {
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      <button onClick={() => setBatch("")} className={`chip py-1.5 px-3 whitespace-nowrap ${!batch ? "bg-[#374B5C] text-white" : "bg-white border border-slate-200"}`} data-testid="invite-batch-all">All batches</button>
      {batches.map((b) => <button key={b.id} onClick={() => setBatch(b.id)} className={`chip py-1.5 px-3 whitespace-nowrap ${batch === b.id ? "bg-[#374B5C] text-white" : "bg-white border border-slate-200"}`} data-testid="invite-batch-chip">
        {b.name} · {b.months}m {b.plan_name} · {b.stats.joined}/{b.count} joined</button>)}
    </div>
  );
}

function InviteRow({ i, reload }) {
  const act = async (path, msg) => { try { await api.post(`/admin/invites/${i.id}/${path}`); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); } };
  const copy = () => { navigator.clipboard.writeText(`${window.location.origin}/invite/${i.token}`); toast.success("Invite link copied"); };
  const live = ["sent", "opened", "expired"].includes(i.status);
  return (
    <tr className="border-t border-slate-100" data-testid="invite-row">
      <td className="p-3"><div className="font-medium">{i.company_name || "—"}</div><div className="text-xs muted">{i.contact_name} · {i.email}</div></td>
      <td className="p-3 text-xs">{i.months}m {i.plan_name}</td>
      <td className="p-3"><StatusBadge status={i.status} />{i.reminded_at && <div className="text-[10px] muted mt-0.5">reminded</div>}</td>
      <td className="p-3 text-xs">{fmtDate(i.sent_at)}{i.resends > 0 && ` · resent ${i.resends}x`}<div className="muted">expires {fmtDate(i.expires_at)}</div></td>
      <td className="p-3"><div className="flex gap-1">
        {live && <button onClick={copy} className="btn-ghost btn-sm" title="Copy link" data-testid="invite-copy-btn"><Copy size={13} /></button>}
        {live && <button onClick={() => act("resend", "Invite resent")} className="btn-ghost btn-sm" data-testid="invite-resend-btn"><RotateCw size={13} />Resend</button>}
        {live && <button onClick={() => window.confirm(`Cancel the invite for ${i.email}?`) && act("cancel", "Invite cancelled")} className="btn-ghost btn-sm" data-testid="invite-cancel-btn"><XCircle size={13} /></button>}
      </div></td>
    </tr>
  );
}

function InviteList({ data, f, setF, reload }) {
  return (
    <section className="space-y-3" data-testid="invite-list">
      <h2 className="font-bold flex items-center gap-2"><Users size={18} />Sent invites</h2>
      <BatchChips batches={data.batches} batch={f.batch_id} setBatch={(b) => setF({ ...f, batch_id: b })} />
      <div className="flex flex-col sm:flex-row gap-2">
        <input className="input" placeholder="Search email, contact or company" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} data-testid="invite-search" />
        <select className="input sm:w-48" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })} data-testid="invite-status-filter"><option value="">All statuses</option>{["sent", "opened", "joined", "expired", "cancelled"].map((s) => <option key={s} value={s}>{s}</option>)}</select>
      </div>
      <div className="card overflow-x-auto"><table className="w-full text-sm"><thead className="bg-slate-50 text-left"><tr><th className="p-3">Provider</th><th className="p-3">Offer</th><th className="p-3">Status</th><th className="p-3">Sent</th><th className="p-3"></th></tr></thead>
        <tbody>{data.invites.map((i) => <InviteRow key={i.id} i={i} reload={reload} />)}</tbody></table>
        {data.invites.length === 0 && <p className="p-5 text-sm muted" data-testid="invite-empty">No invites yet.</p>}</div>
    </section>
  );
}

export default function AdminInvites() {
  const [f, setF] = useState({ batch_id: "", status: "", q: "" });
  const [data, setData] = useState(null);
  const load = () => api.get("/admin/invites", { params: f }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, [JSON.stringify(f)]); // eslint-disable-line
  return (
    <div className="space-y-6">
      <PageTitle title="Invite providers" subtitle="Bring vetted suppliers over with a free membership period." />
      <Composer onSent={load} />
      {data ? <><InviteFunnel batches={data.batches} reload={load} /><InviteList data={data} f={f} setF={setF} reload={load} /></> : <Loader />}
    </div>
  );
}
